import { useState } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Snowflake, Droplets, Zap, TrendingUp, CircleDollarSign, MapPin, Sparkles, Heart } from "lucide-react";

const PRODUCT_IMG_1 = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/t6vxgf6l_2.png";
const PRODUCT_IMG_2 = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/mxe1ob5s_4.png";
const COMPOSITION_IMG = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/9erdytwl_1.png";
const POSTMIX_DRINKS_IMG = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/aw5180xa_Untitled-1sd3.jpg";
const POSTMIX_MACHINE_IMG = "https://customer-assets.emergentagent.com/job_beverage-innovation/artifacts/bjofozej_ULTIMATE%20INFUSIONS-4.png";
const SLUSH_IMG = "https://customer-assets.emergentagent.com/job_17b76ed5-ea8c-4be2-90e7-e5b1550fa22e/artifacts/l3kiuxsb_fun-slush-koncentrat-43961-19.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const fcbFlavours = [
  { name: "Blueberry", color: "#4F46E5" },
  { name: "Cherry", color: "#DC2626" },
  { name: "Cherry Cola", color: "#991B1B" },
  { name: "Cola", color: "#78350F" },
  { name: "Grape", color: "#7C3AED" },
  { name: "Green Apple", color: "#16A34A" },
  { name: "Jal Jeera", color: "#A3A322" },
  { name: "Kala Khatta", color: "#581C87" },
  { name: "Lime", color: "#65A30D" },
  { name: "Lime Lemon", color: "#A3E635" },
  { name: "Mango", color: "#F59E0B" },
  { name: "Mojito", color: "#059669" },
  { name: "Orange", color: "#EA580C" },
  { name: "Orange Mint", color: "#F97316" },
  { name: "Passion Fruit", color: "#E11D48" },
  { name: "Raspberry", color: "#E11D48" },
  { name: "Red Guava Chilli", color: "#B91C1C" },
  { name: "Sour Guava", color: "#84CC16" },
  { name: "Strawberry", color: "#F43F5E" },
  { name: "Watermelon", color: "#EF4444" },
];

const postmixFlavours = [
  { name: "Cola", color: "#78350F" },
  { name: "Cherry Cola", color: "#991B1B" },
  { name: "Green Apple", color: "#16A34A" },
  { name: "Guava", color: "#84CC16" },
  { name: "Jal Jeera", color: "#A3A322" },
  { name: "Lemon Lime", color: "#A3E635" },
  { name: "Lemonade", color: "#FDE047" },
  { name: "Mango", color: "#F59E0B" },
  { name: "Mojito", color: "#059669" },
  { name: "Orange", color: "#EA580C" },
  { name: "Raspberry", color: "#E11D48" },
  { name: "Watermelon", color: "#EF4444" },
];

const fcbFeatures = [
  { icon: Snowflake, title: "Frozen + Carbonated", desc: "Unique combination of freezing and carbonation for superior refreshment." },
  { icon: Zap, title: "High Throughput", desc: "Machines designed for high-footfall locations with rapid dispensing." },
  { icon: TrendingUp, title: "Impulse Purchase", desc: "Strong impulse-driven purchase behaviour drives consistent sales." },
  { icon: CircleDollarSign, title: "Premium Margins", desc: "Low input and logistics cost with strong retail pricing power." },
  { icon: MapPin, title: "Ideal Locations", desc: "Perfect for malls, cinemas, QSRs, events, kiosks, and retail." },
];

const postmixFeatures = [
  { icon: Droplets, title: "Consistent Quality", desc: "Optimal syrup-to-water ratios for consistent taste every time." },
  { icon: Zap, title: "High Volume", desc: "Engineered for high-volume dispensing environments." },
  { icon: CircleDollarSign, title: "Cost Efficient", desc: "Lower logistics cost compared to bottled beverages." },
  { icon: TrendingUp, title: "Scalable", desc: "High scalability for institutional and enterprise clients." },
  { icon: MapPin, title: "Versatile Deployment", desc: "Food courts, corporate cafeterias, QSR chains, hospitality." },
];

const slushFeatures = [
  { icon: Sparkles, title: "Luxuriously Smooth", desc: "Silky smooth frozen flavour experience." },
  { icon: Heart, title: "Bold Flavours", desc: "Deep, long-lasting taste with every sip." },
  { icon: Snowflake, title: "Premium Quality", desc: "Advanced flavour systems and superior texture." },
  { icon: TrendingUp, title: "Modern Appeal", desc: "Sets a new benchmark for frozen drinks." },
  { icon: CircleDollarSign, title: "Rich Indulgence", desc: "Perfectly balanced and refreshingly indulgent." },
];

const slushFlavours = [
  { name: "Blue Raspberry", color: "#3B82F6" },
  { name: "Strawberry", color: "#F43F5E" },
  { name: "Mango", color: "#F59E0B" },
  { name: "Grape", color: "#7C3AED" },
  { name: "Green Apple", color: "#16A34A" },
  { name: "Watermelon", color: "#EF4444" },
  { name: "Orange", color: "#EA580C" },
  { name: "Cherry", color: "#DC2626" },
  { name: "Lemon Lime", color: "#A3E635" },
  { name: "Tropical Punch", color: "#F97316" },
  { name: "Cotton Candy", color: "#EC4899" },
  { name: "Bubblegum", color: "#F472B6" },
];

export default function ProductsSection() {
  const [activeTab, setActiveTab] = useState("fcb");

  return (
    <section id="products" data-testid="products-section" className="py-16 lg:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
          className="max-w-3xl mb-16"
        >
          <motion.p variants={fadeUp} custom={0} className="text-[#C8D616] font-semibold text-sm uppercase tracking-widest mb-3">
            Our Products
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B1D3A] leading-tight mb-6">
            Next-Generation Beverage Solutions
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-base lg:text-lg text-slate-600 leading-relaxed">
            From frozen beverages to post-mix beverages, we deliver premium,
            scalable solutions tailored for the Indian market.
          </motion.p>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
          >
          <TabsList className="bg-white border border-slate-200 rounded-full p-1 w-full sm:w-auto inline-flex mb-12 overflow-x-auto">
            <TabsTrigger
              value="fcb"
              className="rounded-full px-4 sm:px-6 py-2.5 text-xs sm:text-sm font-semibold data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white whitespace-nowrap"
              data-testid="tab-fcb"
            >
              Frozen Beverage
            </TabsTrigger>
            <TabsTrigger
              value="slush"
              className="rounded-full px-4 sm:px-6 py-2.5 text-xs sm:text-sm font-semibold data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white whitespace-nowrap"
              data-testid="tab-slush"
            >
              Slush Beverage
            </TabsTrigger>
            <TabsTrigger
              value="postmix"
              className="rounded-full px-4 sm:px-6 py-2.5 text-xs sm:text-sm font-semibold data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white whitespace-nowrap"
              data-testid="tab-postmix"
            >
              Post Mix Beverage
            </TabsTrigger>
          </TabsList>
          </motion.div>

          {/* FCB Tab */}
          <TabsContent value="fcb" data-testid="fcb-content">
            {/* Two-column layout - text left, image right */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="grid lg:grid-cols-2 gap-8 items-center bg-[#0B1D3A] rounded-3xl overflow-hidden mb-10 p-8 lg:p-12"
            >
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">Frozen Beverage</h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Our FCB range combines carbonation with slush technology to create the ultimate
                  frozen refreshment. Ideal for high-footfall locations, these beverages drive
                  strong impulse purchase behaviour with premium margins.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {fcbFeatures.map((f) => (
                    <div key={f.title} className="flex gap-3 items-start">
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                        <f.icon size={14} className="text-[#C8D616]" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-white">{f.title}</h4>
                        <p className="text-xs text-slate-400">{f.desc.split('.')[0]}.</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <img 
                  src={PRODUCT_IMG_1} 
                  alt="Frozen Carbonated Beverage" 
                  className="w-full max-w-[400px] h-auto object-contain rounded-2xl"
                />
              </div>
            </motion.div>

            {/* Flavours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
            >
              <h3 className="text-xl font-bold text-[#0B1D3A] mb-6">Available Flavours</h3>
              <div className="flex flex-wrap gap-3">
                {fcbFlavours.map((f) => (
                  <Badge
                    key={f.name}
                    variant="outline"
                    className="px-5 py-3 rounded-full text-base font-medium border-slate-200 bg-white hover:shadow-md transition-shadow cursor-default"
                    data-testid={`flavour-${f.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <span className="w-4 h-4 rounded-full mr-2.5 shrink-0" style={{ background: f.color }} />
                    {f.name}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-4">
                Custom flavour development available based on client requirements and minimum order quantities.
              </p>
            </motion.div>
          </TabsContent>

          {/* Slush Beverage Tab */}
          <TabsContent value="slush" data-testid="slush-content">
            {/* Two-column layout - text left, image right */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="grid lg:grid-cols-2 gap-8 items-center bg-[#0B1D3A] rounded-3xl overflow-hidden mb-10 p-8 lg:p-12"
            >
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">Slush Beverage</h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  A premium uncarbonated frozen beverage crafted for a smoother, richer, and more refined experience. 
                  Designed with advanced flavour systems and superior texture, it delivers deep, long-lasting taste 
                  with every sip. Perfectly balanced and refreshingly indulgent, it sets a new benchmark for modern frozen drinks.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {slushFeatures.map((f) => (
                    <div key={f.title} className="flex gap-3 items-start">
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                        <f.icon size={14} className="text-[#C8D616]" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-white">{f.title}</h4>
                        <p className="text-xs text-slate-400">{f.desc.split('.')[0]}.</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <img 
                  src={SLUSH_IMG} 
                  alt="Slush Beverage" 
                  className="w-full max-w-[400px] h-auto object-contain rounded-2xl"
                />
              </div>
            </motion.div>

            {/* Flavours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
            >
              <h3 className="text-xl font-bold text-[#0B1D3A] mb-6">Available Flavours</h3>
              <div className="flex flex-wrap gap-3">
                {slushFlavours.map((f) => (
                  <Badge
                    key={f.name}
                    variant="outline"
                    className="px-5 py-3 rounded-full text-base font-medium border-slate-200 bg-white hover:shadow-md transition-shadow cursor-default"
                    data-testid={`slush-flavour-${f.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <span className="w-4 h-4 rounded-full mr-2.5 shrink-0" style={{ background: f.color }} />
                    {f.name}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-4">
                Custom flavour development available based on client requirements and minimum order quantities.
              </p>
            </motion.div>
          </TabsContent>

          {/* Post Mix Tab */}
          <TabsContent value="postmix" data-testid="postmix-content">
            {/* Two-column layout - text left, image right */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="grid lg:grid-cols-2 gap-8 items-center bg-[#0B1D3A] rounded-3xl overflow-hidden mb-10 p-8 lg:p-12"
            >
              <div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">Post Mix Beverage</h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Engineered for high-volume dispensing environments including food courts,
                  corporate cafeterias, QSR chains, and hospitality venues. Consistent taste
                  with optimal syrup-to-water ratios every time.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {postmixFeatures.map((f) => (
                    <div key={f.title} className="flex gap-3 items-start">
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                        <f.icon size={14} className="text-[#C8D616]" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-white">{f.title}</h4>
                        <p className="text-xs text-slate-400">{f.desc.split('.')[0]}.</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-center lg:justify-end">
                <img 
                  src={POSTMIX_DRINKS_IMG} 
                  alt="Post Mix Beverages" 
                  className="w-full max-w-[400px] h-auto object-contain rounded-2xl"
                />
              </div>
            </motion.div>

            {/* Flavours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
            >
              <h3 className="text-xl font-bold text-[#0B1D3A] mb-6">Available Flavours</h3>
              <div className="flex flex-wrap gap-3">
                {postmixFlavours.map((f) => (
                  <Badge
                    key={f.name}
                    variant="outline"
                    className="px-5 py-3 rounded-full text-base font-medium border-slate-200 bg-white hover:shadow-md transition-shadow cursor-default"
                  >
                    <span className="w-4 h-4 rounded-full mr-2.5 shrink-0" style={{ background: f.color }} />
                    {f.name}
                  </Badge>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-4">
                Custom flavour development available based on client requirements and minimum order quantities.
              </p>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
